import React, { useEffect, useState } from 'react'
import numbro from 'numbro'
import {
  List,
  Datagrid,
  TextField,
  downloadCSV,
  FunctionField,
  Pagination,
  useDataProvider
} from 'react-admin'
import jsonExport from 'jsonexport/dist'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import MuiList from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import { makeStyles } from '@material-ui/core/styles'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import Chip from '@material-ui/core/Chip'
import IconButton from '@material-ui/core/IconButton'
import Menu from '@material-ui/core/Menu'
import MuiTextField from '@material-ui/core/TextField'
import FilterListIcon from '@material-ui/icons/FilterList'
import CloseIcon from '@material-ui/icons/Close'
import Divider from '@material-ui/core/Divider'
import UsageChart from '../components/UsageChart'
import Tooltip from '../components/Tooltip'
import Data from '../components/Data'
import { formatHistoryData } from '../lib/utils'
import * as api from '../data'
import * as storage from '../lib/storage'
import {
  ResponsiveContainer,
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip
} from 'recharts'

const useStyles = makeStyles(theme => ({
  textInput: { width: 400, marginRight: 10 },
  selectInput: { width: 200 },
  filterPanel: {
    marginBottom: 12
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 16
  },
  searchField: {
    flex: '1 1 280px',
    minWidth: 280
  },
  filterActions: {
    display: 'flex',
    gap: 12,
    marginTop: 16
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16
  },
  addFilterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginLeft: 'auto'
  },
  chipsRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 12
  },
  tagSelect: {
    minWidth: 220
  },
  removableFilter: {
    position: 'relative',
    display: 'inline-flex'
  },
  removeFilterButton: {
    position: 'absolute',
    top: -8,
    right: -8,
    zIndex: 1,
    backgroundColor: '#f1f3f4',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    padding: 4
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6
  },
  chip: {
    margin: 2
  },
  field: {
    display: 'flex'
  },
  actions: {
    display: 'flex'
  },
  indicator: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  projectionSection: {
    display: 'flex',
    gap: 24,
    alignItems: 'stretch',
    flexWrap: 'wrap'
  },
  projectionCard: {
    minWidth: 340,
    flex: '0 0 360px',
    borderRadius: 12,
    border: '1px solid #e6eaf0',
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)'
  },
  projectionChartCard: {
    minWidth: 360,
    flex: '1 1 520px',
    borderRadius: 12,
    border: '1px solid #e6eaf0',
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)'
  },
  projectionHeaderEyebrow: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase'
  },
  projectionTitle: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(2)
  },
  projectionStats: {
    display: 'flex',
    gap: 24,
    marginBottom: theme.spacing(2)
  },
  projectionStatLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12
  },
  projectionStatValue: {
    fontWeight: 600
  },
  projectionList: {
    paddingTop: 0,
    paddingBottom: 0
  },
  projectionRow: {
    paddingLeft: 0,
    paddingRight: 0,
    borderBottom: '1px solid #eef2f7'
  },
  projectionRowLast: {
    borderBottom: 'none'
  },
  projectionPrimary: {
    fontWeight: 600
  },
  projectionSecondary: {
    color: theme.palette.text.secondary
  },
  projectionChartWrap: {
    width: '100%',
    height: 300
  },
  projectionEmpty: {
    padding: theme.spacing(2, 0),
    color: theme.palette.text.secondary
  }
}))

const exporter = modalities => {
  const modalitiesForExport = modalities.map(modality => {
    const {
      dashboardIframeUrl,
      logstreamIframeUrl,
      relationshipId,
      metrics,
      relationship,
      ...modalityForExport
    } = modality
    return modalityForExport
  })

  jsonExport(
    modalitiesForExport,
    {
      headers: [
        'id',
        'displayName',
        'magnetType',
        'heLevel',
        'heBoilOff',
        'hePredictedBoilOff',
        'heRefill',
        'heTimeToRefill',
        'heDateToRefill',
        'heCapacity',
        'heCost'
      ]
    },
    (err, csv) => {
      downloadCSV(csv, 'modalities-cryogen-analysis')
    }
  )
}

const normalizeFilters = (filters = {}) => {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      acc[key] = value
    } else if (typeof value === 'string' && value.trim()) {
      acc[key] = value.trim()
    } else if (typeof value === 'number') {
      acc[key] = value
    }

    return acc
  }, {})
}

const hasAppliedFilter = filters => Object.keys(normalizeFilters(filters)).length > 0

const FilterPanel = ({
  draftFilters,
  onDraftChange,
  onApply,
  onClear,
  tagTypes,
  tagOptions,
  visibleOptionalFilters,
  onAddOptionalFilter,
  onRemoveOptionalFilter,
}) => {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState(null)
  const availableOptionalFilters = tagTypes.filter(
    tagType => !visibleOptionalFilters.includes(`tag_${tagType.name}`)
  )
  const isMenuOpen = Boolean(anchorEl)

  return (
    <Card elevation={2} className={classes.filterPanel}>
      <CardContent>
        <div>
          <Typography variant='h6'>Filter Cryogen Analysis</Typography>
          <Typography variant='body2' color='textSecondary'>
            Apply at least one filter before loading results
          </Typography>
        </div>

        <div className={classes.searchRow}>
          <MuiTextField
            label='Search'
            variant='outlined'
            value={draftFilters.q || ''}
            onChange={event => onDraftChange('q', event.target.value)}
            className={classes.searchField}
          />

          <FormControl variant='outlined' className={classes.selectInput}>
            <InputLabel>Active Status</InputLabel>
            <Select
              value={draftFilters.active ?? ''}
              onChange={event => onDraftChange('active', event.target.value)}
              label='Active Status'
            >
              <MenuItem value=''>Show All</MenuItem>
              <MenuItem value={1}>Only Active</MenuItem>
              <MenuItem value={0}>Only Inactive</MenuItem>
            </Select>
          </FormControl>

          {tagTypes
            .filter(tagType => visibleOptionalFilters.includes(`tag_${tagType.name}`))
            .map(tagType => {
              const source = `tag_${tagType.name}`
              const selectedIds = draftFilters[source] || []
              const options = tagOptions[tagType.id] || []

              return (
                <div key={tagType.id} className={classes.removableFilter}>
                  <IconButton
                    size='small'
                    className={classes.removeFilterButton}
                    onClick={() => onRemoveOptionalFilter(source)}
                  >
                    <CloseIcon fontSize='small' />
                  </IconButton>
                  <FormControl variant='outlined' className={classes.tagSelect}>
                    <InputLabel>{`Tag: ${tagType.name}`}</InputLabel>
                    <Select
                      multiple
                      value={selectedIds}
                      onChange={event => {
                        onDraftChange(source, event.target.value)
                      }}
                      label={`Tag: ${tagType.name}`}
                      renderValue={selected => (
                        <div className={classes.chips}>
                          {selected.map(id => {
                            const option = options.find(tag => tag.id === id)
                            return (
                              <Chip
                                key={id}
                                label={option ? option.name : id}
                                size='small'
                                className={classes.chip}
                              />
                            )
                          })}
                        </div>
                      )}
                    >
                      {options.map(option => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )
            })}

          <div className={classes.addFilterRow}>
            <IconButton
              onClick={event => setAnchorEl(event.currentTarget)}
              disabled={availableOptionalFilters.length === 0}
            >
              <FilterListIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              keepMounted
              open={isMenuOpen}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {availableOptionalFilters.map(tagType => (
                <MenuItem
                  key={tagType.id}
                  onClick={() => {
                    onAddOptionalFilter(`tag_${tagType.name}`)
                    setAnchorEl(null)
                  }}
                >
                  {`Tag: ${tagType.name}`}
                </MenuItem>
              ))}
            </Menu>
          </div>
        </div>

        <div className={classes.filterActions}>
          <Button
            variant='contained'
            color='primary'
            onClick={onApply}
            disabled={!hasAppliedFilter(draftFilters)}
          >
            Apply Filters
          </Button>
          <Button variant='outlined' onClick={onClear}>
            Clear
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

const ChartIndicator = modality => {
  const classes = useStyles()
  const [data, setData] = useState()
  const [loading, setLoading] = useState(false)

  const fetch = async () => {
    try {
      setLoading(true)
      const res = await api.modalities.fetchUsageHistory(modality.id)
      const chartData = formatHistoryData(res.history || {})
      setData(chartData)
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const onOpen = () => {
    if (data) return
    fetch()
  }

  return (
    <Tooltip
      onOpen={onOpen}
      interactive
      disableFocusListener
      title={
        <div style={{ width: 300 }}>
          <Typography variant='body2'>Helium Level History</Typography>
          <div
            className={classes.root}
            style={{
              backgroundColor: '#fff',
              borderRadius: 4,
              paddingBottom: 16,
              marginBottom: 8,
              marginTop: 8,
              minHeight: 200,
              color: '#999',
              fontSize: 14,
              justifyContent: 'center',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {loading && !data ? (
              <Typography variant='subtitle1' color='primary'>
                Loading chart data...
              </Typography>
            ) : (
              <UsageChart data={data} />
            )}
          </div>
        </div>
      }
      arrow
    >
      <div className={classes.indicator}>{modality.heLevel}</div>
    </Tooltip>
  )
}

const Projection = ({ data }) => {
  const quarters = (data || []).reduce((acc, item) => {
    if (item && item.heDateToRefill) {
      const quarter = item.heDateToRefill

      if (item.heCost) {
        acc[quarter] = acc[quarter] || {}

        acc[quarter]['cost'] = acc[quarter]['cost'] || 0
        acc[quarter]['cost'] += numbro.unformat(item.heCost)

        acc[quarter]['liters'] = acc[quarter]['liters'] || 0
        acc[quarter]['liters'] += item.heLiters
      }
    }

    return acc
  }, {})

  const list = Object.keys(quarters).sort((a, b) => {
    const [x1, y1] = a.split(' ')
    const [x2, y2] = b.split(' ')
    return Number(y1 + x1.replace('Q', '')) - Number(y2 + x2.replace('Q', ''))
  })
  const classes = useStyles()
  const totalCost = list.reduce((sum, key) => sum + (quarters[key].cost || 0), 0)
  const totalLiters = list.reduce((sum, key) => sum + (quarters[key].liters || 0), 0)
  const chartData = list.map(name => ({ name, value: quarters[name].cost }))

  return data ? (
    <Box
      mt={4}
      mb={4}
      className={classes.projectionSection}
    >
      <Card elevation={0} className={classes.projectionCard}>
        <CardContent>
          <Typography className={classes.projectionHeaderEyebrow}>
            Projection Summary
          </Typography>
          <Typography variant='h6' className={classes.projectionTitle}>
            Quarterly Projection
          </Typography>

          <div className={classes.projectionStats}>
            <div>
              <Typography className={classes.projectionStatLabel}>
                Total projected cost
              </Typography>
              <Typography variant='h6' className={classes.projectionStatValue}>
                {numbro(totalCost).formatCurrency()}
              </Typography>
            </div>
            <div>
              <Typography className={classes.projectionStatLabel}>
                Total projected liters
              </Typography>
              <Typography variant='h6' className={classes.projectionStatValue}>
                {numbro(totalLiters).format({ thousandSeparated: true, mantissa: 0 })}
              </Typography>
            </div>
          </div>

          <Divider />

          {list.length ? (
            <MuiList className={classes.projectionList}>
              {list.map((key, index) => (
                <ListItem
                  key={'quarter-item' + key}
                  className={`${classes.projectionRow} ${index === list.length - 1 ? classes.projectionRowLast : ''}`}
                >
                  <ListItemText
                    primary={key}
                    secondary={`${numbro(quarters[key].liters).format({
                      thousandSeparated: true,
                      mantissa: 0
                    })} liters`}
                    primaryTypographyProps={{ className: classes.projectionPrimary }}
                    secondaryTypographyProps={{ className: classes.projectionSecondary }}
                  />
                  <ListItemSecondaryAction>
                    <Typography variant='subtitle1' className={classes.projectionStatValue}>
                      {numbro(quarters[key].cost).formatCurrency()}
                    </Typography>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </MuiList>
          ) : (
            <Typography variant='body2' className={classes.projectionEmpty}>
              No projected refill costs are available for the current result set.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card elevation={0} className={classes.projectionChartCard}>
        <CardContent>
          <Typography className={classes.projectionHeaderEyebrow}>
            Spend Trend
          </Typography>
          <Typography variant='h6' className={classes.projectionTitle}>
            Cost Projection
          </Typography>

          <div className={classes.projectionChartWrap}>
            <ResponsiveContainer width='100%' height='100%'>
              <RechartsBarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray='3 3' stroke='#edf2f7' vertical={false} />
                <XAxis dataKey='name' tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  tickFormatter={value => numbro(value).formatCurrency()}
                  axisLine={false}
                  tickLine={false}
                  width={90}
                />
                <RechartsTooltip
                  cursor={{ fill: 'rgba(25, 118, 210, 0.08)' }}
                  formatter={value => [numbro(value).formatCurrency(), 'Cost']}
                  contentStyle={{
                    borderRadius: 10,
                    border: '1px solid #d9e2ec',
                    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)'
                  }}
                />
                <Bar dataKey='value' fill='#1976d2' radius={[8, 8, 0, 0]} />
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </Box>
  ) : null
}

const ModalityList = props => {
  const [data, setData] = useState([])
  const classes = useStyles()
  const dataProvider = useDataProvider()
  const accountId = storage.get('user', {})?.accountId
  const [tagTypes, setTagTypes] = useState([])
  const [tagOptions, setTagOptions] = useState({})
  const [draftFilters, setDraftFilters] = useState({})
  const [appliedFilters, setAppliedFilters] = useState({})
  const [visibleOptionalFilters, setVisibleOptionalFilters] = useState([])

  useEffect(() => {
    const fetchTagFilters = async () => {
      try {
        const { data: tagTypeData } = await dataProvider.getList('tag-types', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'name', order: 'ASC' },
          filter: accountId ? { accountId } : {}
        })

        setTagTypes(tagTypeData || [])

        const tagResponses = await Promise.all(
          (tagTypeData || []).map(async tagType => {
            const { data: tags } = await dataProvider.getList('tags', {
              pagination: { page: 1, perPage: 1000 },
              sort: { field: 'name', order: 'ASC' },
              filter: accountId
                ? { tagTypeId: tagType.id, accountId }
                : { tagTypeId: tagType.id }
            })

            return [tagType.id, tags || []]
          })
        )

        setTagOptions(Object.fromEntries(tagResponses))
      } catch (error) {
        console.error('Error loading cryogen analysis filters:', error)
      }
    }

    fetchTagFilters()
  }, [accountId, dataProvider])

  const handleDraftChange = (key, value) => {
    setDraftFilters(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const handleApplyFilters = () => {
    const nextFilters = normalizeFilters(draftFilters)
    setAppliedFilters(nextFilters)
    if (!hasAppliedFilter(nextFilters)) {
      setData([])
    }
  }

  const handleClearFilters = () => {
    setDraftFilters({})
    setAppliedFilters({})
    setVisibleOptionalFilters([])
    setData([])
  }

  const handleAddOptionalFilter = source => {
    setVisibleOptionalFilters(prev =>
      prev.includes(source) ? prev : [...prev, source]
    )
  }

  const handleRemoveOptionalFilter = source => {
    setVisibleOptionalFilters(prev => prev.filter(item => item !== source))
    setDraftFilters(prev => {
      const next = { ...prev }
      delete next[source]
      return next
    })
  }

  const shouldLoadResults = hasAppliedFilter(appliedFilters)

  return (
    <>
      <FilterPanel
        draftFilters={draftFilters}
        onDraftChange={handleDraftChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        tagTypes={tagTypes}
        tagOptions={tagOptions}
        visibleOptionalFilters={visibleOptionalFilters}
        onAddOptionalFilter={handleAddOptionalFilter}
        onRemoveOptionalFilter={handleRemoveOptionalFilter}
      />

      {shouldLoadResults ? (
        <>
          <List
            key={JSON.stringify(appliedFilters)}
            title='Modality Cryogen Analysis'
            exporter={exporter}
            bulkActionButtons={false}
            basePath='/modalities/cryogen-analysis'
            resource='modalities/cryogen-analysis'
            filter={appliedFilters}
            sort={{ field: 'heActualBoilOff', order: 'DESC' }}
            perPage={25}
            pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
            {...props}
          >
            <Data onData={setData}>
              <Datagrid
                size='small'
                rowStyle={(record, index) => ({
                  opacity: record.timeStatusColor === 'red' ? 0.5 : 1
                })}
              >
                <TextField label='Modality' source='displayName' />
                <TextField label='Magnet Type' source='magnetType' />
                <FunctionField
                  label='He Level'
                  source='heLevel'
                  render={props => <ChartIndicator {...props} />}
                />

                <FunctionField
                  label='Predicted Boil-off (mo)'
                  source='hePredictedBoilOff'
                  render={props => (
                    <span
                      {...props}
                      style={{
                        color:
                          props.hePredictedBoilOff > props.heBoilOffGuide
                            ? '#a50e0e'
                            : 'inherit'
                      }}
                    >
                      {props.hePredictedBoilOff}
                    </span>
                  )}
                />

                <FunctionField
                  label='Actual Boil-off (mo)'
                  source='heActualBoilOff'
                  render={props => (
                    <span
                      style={{
                        color:
                          props.heActualBoilOff > props.heBoilOffGuide
                            ? '#a50e0e'
                            : 'inherit'
                      }}
                    >
                      {props.heActualBoilOff}
                    </span>
                  )}
                />

                <TextField label='Annualized HE loss (L)' source='heAnualLoss' />
                <TextField label='Refill Threshold' source='heRefill' />
                <TextField label='Time to Refill' source='heTimeToRefill' />
                <TextField label='Date to Refill' source='heDateToRefill' />
                <TextField label='Capacity (L)' source='heCapacity' />
                <TextField label='Cost' source='heCost' />
              </Datagrid>
            </Data>
          </List>

          <Projection data={data} />
        </>
      ) : (
        <Card elevation={1} className={classes.filterPanel}>
          <CardContent>
            <Typography variant='body1'>
              Apply at least one filter to load cryogen analysis results.
            </Typography>
          </CardContent>
        </Card>
      )}
    </>
  )
}

export default ModalityList
